import { Box, Button, Drawer, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import connector from 'components/@home/@people/connector';
import ImportPreview from 'components/@home/@people/ImportPreview';
import ImportsTable from 'components/@home/@people/ImportsTable';
import BackButton from 'components/@home/drawers/BackButton';
import CloseButton from 'components/@home/drawers/CloseButton';
import Alert from 'components/alerts/Alert';
import Loading from 'components/common/Loading';
import deferRender from 'defer-render-hoc';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import { arrayOf, bool, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Socket from 'services/Socket';
import { I18n } from 'utils/i18n';
import employeeShape from 'shapes/employee';

const SyncIcon = ({ color }) => (
  <svg
    width="55"
    height="40"
    viewBox="0 0 55 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M13.75 40C9.95833 40 6.71875 38.6875 4.03125 36.0625C1.34375 33.4375 0 30.2292 0 26.4375C0 23.1875 0.979167 20.2917 2.9375 17.75C4.89583 15.2083 7.45833 13.5833 10.625 12.875C11.6667 9.04167 13.75 5.9375 16.875 3.5625C20 1.1875 23.5417 0 27.5 0C32.375 0 36.5104 1.69792 39.9062 5.09375C43.3021 8.48958 45 12.625 45 17.5V17.5V17.5C47.875 17.8333 50.2604 19.0729 52.1562 21.2188C54.0521 23.3646 55 25.875 55 28.75C55 31.875 53.9062 34.5312 51.7188 36.7188C49.5312 38.9062 46.875 40 43.75 40H30C28.625 40 27.4479 39.5104 26.4688 38.5312C25.4896 37.5521 25 36.375 25 35V22.125L21 26L17.5 22.5L27.5 12.5L37.5 22.5L34 26L30 22.125V35V35V35H43.75C45.5 35 46.9792 34.3958 48.1875 33.1875C49.3958 31.9792 50 30.5 50 28.75C50 27 49.3958 25.5208 48.1875 24.3125C46.9792 23.1042 45.5 22.5 43.75 22.5H40V17.5C40 14.0417 38.7812 11.0938 36.3438 8.65625C33.9062 6.21875 30.9583 5 27.5 5C24.0417 5 21.0938 6.21875 18.6562 8.65625C16.2188 11.0938 15 14.0417 15 17.5H13.75C11.3333 17.5 9.27083 18.3542 7.5625 20.0625C5.85417 21.7708 5 23.8333 5 26.25C5 28.6667 5.85417 30.7292 7.5625 32.4375C9.27083 34.1458 11.3333 35 13.75 35H20V40H13.75V40M27.5 22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5V22.5"
      fill={color}
    />
  </svg>
);

const BAR_CONTAINER_HEIGHT = 30;

const getSyncBarSx = (delay, primaryColor) => ({
  width: 6,
  borderRadius: '9999px',
  bgcolor: primaryColor
    ? delay === 0
      ? alpha(primaryColor, 0.5)
      : delay === 0.5
        ? primaryColor
        : alpha(primaryColor, 0.7)
    : 'primary.main',
  '@keyframes syncBarPulse': {
    '0%, 100%': { height: 12, opacity: 0.7 },
    '50%': { height: BAR_CONTAINER_HEIGHT, opacity: 1 },
  },
  animation: `syncBarPulse 1.8s ease-in-out ${delay}s infinite`,
});

const SyncBars = ({ primaryColor }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: 0.5,
      height: BAR_CONTAINER_HEIGHT,
      flexShrink: 0,
    }}
  >
    <Box sx={getSyncBarSx(0, primaryColor)} />
    <Box sx={getSyncBarSx(0.5, primaryColor)} />
    <Box sx={getSyncBarSx(1, primaryColor)} />
  </Box>
);

const autoHideDuration = ms('10 seconds');

const getStyles = theme => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 'calc(100% - 40px)',
    },
  },
  close: {
    paddingTop: '20px',
    paddingLeft: '20px',
    flexShrink: 0,
  },
  mainColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  content: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loading: {
    flexGrow: 1,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    p: '32px',
    isolation: 'isolate',
    maxWidth: 518,
    gap: '32px',
  },
  progressIconArea: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    py: '64px',
    width: 320,
    maxWidth: 320,
    height: 320,
    flex: 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      background: alpha(theme.palette.primary.main, 0.05),
      borderRadius: '9999px',
      zIndex: 0,
    },
  },
  progressIconCard: {
    position: 'relative',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: 192,
    height: 192,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[2],
    borderRadius: '16px',
    zIndex: 1,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '1px',
      right: '1px',
      top: '1px',
      bottom: '1px',
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
        theme.palette.primary.main,
        0,
      )} 100%)`,
      borderRadius: '15px',
      zIndex: 0,
    },
  },
  progressIconInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    width: 55,
    flex: 'none',
    zIndex: 2,
  },
  progressContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '16px',
    width: '100%',
    maxWidth: 454,
    flex: 'none',
    alignSelf: 'stretch',
  },
  progressTitleRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    width: '100%',
    gap: '12px',
  },
  progressTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
  },
  progressTitle: {
    fontWeight: 700,
    fontSize: 20,
    lineHeight: '28px',
    color: theme.palette.text.primary,
  },
  progressSubtitle: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '20px',
    color: theme.palette.text.secondary,
  },
  progressPct: {
    fontWeight: 700,
    fontSize: 18,
    lineHeight: '18px',
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  progressBarTrack: {
    width: '100%',
    height: 12,
    background: theme.palette.grey[200],
    borderRadius: '9999px',
    overflow: 'hidden',
    position: 'relative',
  },
  progressBarFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    background: theme.palette.primary.main,
    borderRadius: '9999px',
    transition: 'width 0.2s ease',
  },
  progressSecurity: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    '& .MuiSvgIcon-root': {
      fontSize: 12.25,
      color: theme.palette.text.secondary,
    },
  },
  progressSecurityText: {
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '16px',
    color: theme.palette.text.secondary,
  },
  drawerFooter: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    p: 2,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
});

const ImportsDrawer = ({
  actions,
  employeeImports = null,
  employees,
  isLoading,
  isOpen,
  file = null,
  importPreview = null,
  fileData = null,
  loading,
  progress = null,
  currentImportId = null,
  summary = null,
  status: resultStatus = 'created',
  error = null,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const primaryMain = theme.palette.primary.main;
  const [alertOpen, setAlertOpen] = useState(!!summary);

  useEffect(() => {
    if (!currentImportId || !Socket.initialized()) return () => {};
    Socket.emit('subscribe:importProgress', { importId: currentImportId });
    return () => {
      Socket.emit('unsubscribe:importProgress', { importId: currentImportId });
    };
  }, [currentImportId]);

  useEffect(() => {
    if (error) {
      actions.alert.show({
        label: I18n.t('ImportUsers.Error'),
        message: I18n.t('ImportUsers.AnUnexpectedErrorOccurredWhileImporting'),
        variant: 'error',
        autoHideDuration: ms('5 seconds'),
      });
    }
  }, [actions.alert, error]);
  useEffect(() => {
    if (summary && !alertOpen) {
      setAlertOpen(true);
    }
  }, [alertOpen, summary]);
  const closeAlert = () => setAlertOpen(false);
  useEffect(() => {
    if (isOpen) {
      if (isEmpty(employees)) {
        actions.companies.listEmployees();
      }
      if (employeeImports === null) {
        actions.employeeImports.list();
      }
    }
  }, [actions.companies, actions.employeeImports, employeeImports, employees, isOpen]);

  if (isLoading) {
    return (
      <Box sx={styles.loading}>
        <Loading size={56} />
      </Box>
    );
  }

  const handleContinue = async ({ _id }) => {
    await actions.employeeImports.resume(_id);
  };

  const handleCancel = ({ _id }) => {
    actions.employeeImports.cancel(_id);
  };

  const handleStart = ({ _id }) => {
    actions.employeeImports.start(_id);
  };

  const handleShowResults = ({ _id }, status) => {
    actions.employeeImports.showResults(_id, status);
  };

  const handleCancelDryRun = () => {
    if (currentImportId && Socket.initialized()) {
      Socket.emit('cancel:importProgress', { importId: currentImportId });
    }
  };

  return (
    <Drawer
      slotProps={{ paper: { sx: styles.root } }}
      anchor="right"
      open={isOpen}
      onClose={actions.drawers.employeeImports.close}
    >
      <Box sx={styles.close}>
        {importPreview ? (
          <BackButton onClick={actions.drawers.employeeImports.closePreview} />
        ) : (
          <CloseButton onClick={actions.drawers.employeeImports.close} />
        )}
      </Box>
      <Box sx={styles.mainColumn}>
        <Box sx={styles.content}>
          {loading && (
            <Box sx={styles.progressContainer}>
              <Box sx={styles.progressIconArea}>
                <Box sx={styles.progressIconCard}>
                  <Box sx={styles.progressIconInner}>
                    <SyncIcon color={primaryMain} />
                    <SyncBars primaryColor={primaryMain} />
                  </Box>
                </Box>
              </Box>
              <Box sx={styles.progressContent}>
                <Box sx={styles.progressTitleRow}>
                  <Box sx={styles.progressTitleBlock}>
                    <Typography sx={styles.progressTitle}>
                      {I18n.t('ImportsScene.Synchronizing Data')}
                    </Typography>
                    <Typography sx={styles.progressSubtitle}>
                      {progress?.processed != null && progress?.total != null
                        ? I18n.t('ImportsScene.Processing rows', {
                            processed: progress.processed,
                            total: progress.total,
                          })
                        : progress?.phase === 'downloading'
                          ? I18n.t('ImportsScene.Downloading file')
                          : progress?.phase === 'parsing'
                            ? I18n.t('ImportsScene.Parsing file')
                            : I18n.t('ImportsScene.Preparing import')}
                    </Typography>
                  </Box>
                  {progress?.percent != null && (
                    <Typography sx={styles.progressPct}>{progress.percent}%</Typography>
                  )}
                </Box>
                <Box sx={styles.progressBarTrack}>
                  <Box
                    sx={{
                      ...styles.progressBarFill,
                      width: progress?.percent != null ? `${progress.percent}%` : '0%',
                    }}
                  />
                </Box>
                <Box sx={styles.progressSecurity}>
                  <LockIcon />
                  <Typography sx={styles.progressSecurityText}>
                    {I18n.t('ImportsScene.Secure sync message')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          {!loading && !importPreview && employeeImports && (
            <ImportsTable
              employeeImports={employeeImports}
              onContinue={handleContinue}
              onDiscard={handleCancel}
              onClickShowResults={handleShowResults}
            />
          )}
          {!loading && importPreview && (
            <ImportPreview
              actions={actions}
              file={file}
              importPreview={importPreview}
              downloadResumedImport={actions.employeeImports.downloadResumedImport}
              fileData={fileData}
              onCancel={handleCancel}
              onStart={handleStart}
              summary={summary}
              resultStatus={resultStatus}
            />
          )}
        </Box>
        {loading && (
          <Box sx={styles.drawerFooter}>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelDryRun}
              disabled={!currentImportId}
            >
              {I18n.t('ImportsScene.Cancel Process')}
            </Button>
          </Box>
        )}
      </Box>
      <Alert
        isOpen={alertOpen}
        onClose={closeAlert}
        variant="success"
        label={I18n.t('PeoplesScene.Import employees')}
        caption={I18n.t('ImportUsers.CreatedUpdated', summary || { created: 0, updated: 0 })}
        autoHideDuration={autoHideDuration}
      >
        {I18n.t('ImportUsers.Success')}
      </Alert>
    </Drawer>
  );
};

ImportsDrawer.propTypes = {
  actions: object.isRequired,
  employees: arrayOf(employeeShape).isRequired,
  isLoading: bool.isRequired,
  employeeImports: arrayOf(object),
  isOpen: bool.isRequired,
  importPreview: object,
  file: object,
  fileData: object,
  loading: bool.isRequired,
  progress: object,
  currentImportId: string,
  summary: object,
  status: string,
  error: object,
};

export default deferRender(connector(ImportsDrawer));
